import React, { useCallback, useState } from "react";

import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import { ClickAwayListener } from "@mui/material";

import { useTheme } from "@fartherfinance/frontend/theme/ThemeProvider";

import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import TextInput from "@src/sharedComponents/TextInput/TextInput";
import Text from "@src/sharedComponents/Typography/Text";

import styles from "./EditableTradingGroupName.module.css";

interface Props {
  assignmentGroupName: string;
  inputValue: string;
  setEditingNameInput: (value: string) => void;
  saveNewTradingGroupName: (value: string) => Promise<void>;
  isMutating: boolean;
}

const MAX_NAME_LENGTH = 60;

const EditableTradingGroupNameV2: React.FC<Props> = ({
  assignmentGroupName,
  inputValue,
  saveNewTradingGroupName,
  setEditingNameInput,
  isMutating,
}) => {
  const [editingName, setEditingName] = useState<boolean>(false);
  const [hoveringOverInput, setHoveringOverInput] = useState<boolean>(false);

  const {
    color: { $text, $textSuccess },
  } = useTheme();

  const statusNotification = useStatusNotification();

  const onKeyPressEditNewPlanName = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onSaveNewTradingGroupName();
    }
  };

  const isTradingGroupNameValid = inputValue.length > 3;

  const onSaveNewTradingGroupName = async () => {
    if (!isTradingGroupNameValid) {
      setEditingName(false);
      statusNotification("New Trading group name invalid", "Error");
      return;
    }

    await saveNewTradingGroupName(inputValue);
    setEditingName(false);
  };

  const enableEditName = useCallback(() => {
    if (editingName) {
      return;
    }

    setEditingNameInput(assignmentGroupName);
    setEditingName(true);
  }, [assignmentGroupName, editingName, setEditingNameInput]);

  const cancelEditing = useCallback(() => {
    if (!editingName) {
      return;
    }

    setEditingName(false);

    setEditingNameInput("");
  }, [editingName, setEditingNameInput]);

  return (
    <ClickAwayListener mouseEvent={"onMouseDown"} onClickAway={cancelEditing}>
      <div
        className={styles.editablePlanName_title}
        onMouseEnter={() => setHoveringOverInput(true)}
        onMouseLeave={() => setHoveringOverInput(false)}
      >
        {editingName ? (
          <>
            <TextInput
              value={inputValue}
              onChange={(e) => {
                if (e.target.value.length <= MAX_NAME_LENGTH) {
                  setEditingNameInput(e.target.value);
                }
              }}
              onKeyDown={onKeyPressEditNewPlanName}
              error={!isTradingGroupNameValid}
              helperText={
                !isTradingGroupNameValid
                  ? "Name must have more than 3 letters"
                  : ""
              }
              style={{ width: "250px", color: $text }}
              inputProps={{ style: { fontSize: 14 } }}
              disabled={isMutating}
            />

            <CheckCircleIcon
              className={styles.editablePlanName_edit_icon}
              style={{ color: $textSuccess }}
              onClick={onSaveNewTradingGroupName}
            />

            <CancelIcon
              className={styles.editablePlanName_edit_icon}
              style={{ color: $text }}
              onClick={cancelEditing}
            />
          </>
        ) : isMutating ? (
          <Skeleton width={200} />
        ) : (
          <Text onClick={enableEditName}>{assignmentGroupName}</Text>
        )}

        {hoveringOverInput && !editingName && (
          <EditIcon
            className={styles.editablePlanName_edit_icon}
            onClick={enableEditName}
          />
        )}
      </div>
    </ClickAwayListener>
  );
};

export default EditableTradingGroupNameV2;
